import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { Pill, PlusCircle, Package, Search, Bell, ShoppingBag, Settings, TrendingUp, X, LogOut, Moon, Sun, User } from 'lucide-react';
import axios from 'axios';

// استيراد الصفحات
import Inventory from './pages/Inventory';
import AddProduct from './pages/AddProduct';
import OrdersAdmin from './pages/OrdersAdmin'; 
import AdminSettings from './pages/AdminSettings'; 
import Stats from './pages/Stats';
import Login from './pages/Login';
import Profile from './pages/Profile';
import CustomerStore from './components/customer/CustomerStore'; 

// مكون عنصر التنقل (تم تعديله ليدعم العرض في الجوال والكمبيوتر)
const NavItem = ({ to, icon: Icon, label, badgeCount, mobile }) => {
  const location = useLocation();
  const active = location.pathname === to;
  
  // تصميم الجوال (أيقونة وتحتها نص صغير)
  if (mobile) {
    return (
      <Link 
        to={to} 
        className={`relative flex flex-col items-center justify-center flex-1 py-2 transition-all ${
          active ? 'text-blue-600 scale-110' : 'text-gray-400'
        }`}
      >
        <Icon size={20} strokeWidth={active ? 2.5 : 2} />
        <span className="text-[10px] font-bold mt-1">{label}</span>
        {badgeCount > 0 && (
          <span className="absolute top-1 right-1/4 flex h-4 w-4 items-center justify-center bg-red-500 text-white text-[8px] font-black rounded-full border border-white">
            {badgeCount}
          </span>
        )}
      </Link>
    );
  }

  // تصميم الكمبيوتر (Sidebar)
  return (
    <Link 
      to={to} 
      className={`relative flex items-center gap-3 px-5 py-3 rounded-[15px] transition-all duration-300 ${
        active 
        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
        : 'text-gray-500 hover:bg-blue-50 dark:hover:bg-slate-800 hover:text-blue-600 dark:text-slate-400'
      }`}
    >
      <Icon size={20} strokeWidth={active ? 2.5 : 2} />
      <span className="text-sm font-bold">{label}</span>
      {badgeCount > 0 && (
        <span className="absolute left-4 flex h-5 w-5 items-center justify-center bg-red-500 text-white text-[10px] font-black rounded-full border-2 border-white dark:border-slate-900">
          {badgeCount}
        </span>
      )}
    </Link>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(localStorage.getItem('isAdmin') === 'true');
  const [darkMode, setDarkMode] = useState(localStorage.getItem('theme') === 'dark');

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminUser');
    setIsAuthenticated(false);
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={<CustomerStore />} />
        <Route path="/admin-login" element={isAuthenticated ? <Navigate to="/admin" /> : <Login onLogin={setIsAuthenticated} />} />
        <Route 
          path="/admin/*" 
          element={
            isAuthenticated ? (
              <AdminLayout 
                darkMode={darkMode} 
                setDarkMode={setDarkMode} 
                handleLogout={handleLogout} 
                isAuthenticated={isAuthenticated}
              />
            ) : (
              <Navigate to="/admin-login" />
            )
          } 
        />
      </Routes>
    </Router>
  );
}

const AdminLayout = ({ darkMode, setDarkMode, handleLogout, isAuthenticated }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);

  // تحديث مسار الـ API ليعمل على رندر (تأكد من تبديله برابطك)
  const API_URL = "https://my-pharmacy-api.onrender.com"; 

  useEffect(() => {
    if (!isAuthenticated) return;
    const checkOrders = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/orders`);
        const pending = res.data.filter(order => order.payment_status !== 'تم الدفع').length;
        if (pending > newOrdersCount) {
          setShowNotification(true);
          new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3').play().catch(() => {});
        }
        setNewOrdersCount(pending);
      } catch (err) { console.error("خطأ في مراقبة الطلبات"); }
    };
    const interval = setInterval(checkOrders, 10000);
    return () => clearInterval(interval);
  }, [newOrdersCount, isAuthenticated]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-slate-950 font-sans md:pr-72 pb-20 md:pb-0 transition-colors duration-300" dir="rtl">
      
      {/* التنبيه العائم */}
      {showNotification && (
        <div className="fixed top-20 left-4 right-4 md:right-auto md:left-6 z-[100] bg-blue-600 text-white p-4 rounded-2xl shadow-2xl flex justify-between items-center animate-in slide-in-from-top">
          <div className="flex items-center gap-3">
            <ShoppingBag size={20}/>
            <p className="text-sm font-bold">يوجد طلب دواء جديد!</p>
          </div>
          <button onClick={() => setShowNotification(false)}><X size={18}/></button>
        </div>
      )}

      {/* الهيدر العلوي */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/90 backdrop-blur-md border-b border-gray-100 dark:border-slate-800 px-4 md:px-8 py-4 flex justify-between items-center">
        <h1 className="text-lg md:text-xl font-black text-gray-900 dark:text-white flex items-center gap-2">
          <span className="bg-blue-600 p-1.5 rounded-lg text-white"><Pill size={18} /></span>
          <span className="hidden xs:block text-pink-500">صيدلية ميسون</span>
        </h1>
        
        <div className="flex items-center gap-2 md:gap-4">
          <button onClick={() => setDarkMode(!darkMode)} className="p-2 bg-gray-100 dark:bg-slate-800 rounded-xl text-gray-500 dark:text-yellow-400">
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          
          <div className="hidden sm:flex items-center bg-gray-100 dark:bg-slate-800 rounded-xl px-3 py-1.5 gap-2 border border-transparent focus-within:border-blue-500">
              <Search size={16} className="text-gray-400" />
              <input type="text" placeholder="بحث سريع..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-transparent border-none outline-none text-xs font-bold dark:text-white w-24 md:w-auto" />
          </div>

          <button onClick={handleLogout} className="md:hidden p-2 text-red-500 bg-red-50 dark:bg-red-950/30 rounded-xl">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* القائمة الجانبية (للكمبيوتر فقط) */}
      <aside className="hidden md:flex w-72 bg-white dark:bg-slate-900 border-l border-gray-100 dark:border-slate-800 p-6 flex-col fixed right-0 top-0 h-full z-50">
        <div className="mb-8 font-black text-blue-600 border-b pb-4">لوحة التحكم</div>
        <nav className="space-y-2 flex-1">
          <NavItem to="/admin" icon={Package} label="المخزن" />
          <NavItem to="/admin/add" icon={PlusCircle} label="إضافة دواء" />
          <NavItem to="/admin/orders" icon={ShoppingBag} label="الطلبات" badgeCount={newOrdersCount} />
          <NavItem to="/admin/stats" icon={TrendingUp} label="الإحصائيات" />
          <NavItem to="/admin/settings" icon={Settings} label="إعدادات QR شام كاش" />
          <NavItem to="/admin/profile" icon={User} label="حسابي" />
        </nav>
        <button onClick={handleLogout} className="mt-auto p-3 text-red-600 bg-red-50 rounded-xl font-bold flex justify-center gap-2">
          <LogOut size={16} /> خروج
        </button>
      </aside>

      {/* شريط التنقل السفلي (للجوال فقط) */}
      {/* شريط التنقل السفلي للجوال - يحتوي على جميع الأقسام بدون استثناء */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-gray-100 dark:border-slate-800 py-2 flex justify-between items-center z-50 shadow-2xl overflow-x-auto no-scrollbar">
        <div className="flex w-full min-w-max justify-around px-2 gap-1">
          <NavItem mobile to="/admin" icon={Package} label="المخزن" />
          <NavItem mobile to="/admin/add" icon={PlusCircle} label="إضافة" />
          <NavItem mobile to="/admin/orders" icon={ShoppingBag} label="الطلبات" badgeCount={newOrdersCount} />
          <NavItem mobile to="/admin/stats" icon={TrendingUp} label="الإحصائيات" />
          <NavItem mobile to="/admin/settings" icon={Settings} label="الإعدادات" />
          <NavItem mobile to="/admin/profile" icon={User} label="حسابي" />
        </div>
      </nav>
      <main className="p-4 md:p-10 max-w-6xl mx-auto">
        <Routes>
          <Route index element={<Inventory searchTerm={searchTerm} />} />
          <Route path="add" element={<AddProduct />} />
          <Route path="orders" element={<OrdersAdmin />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="stats" element={<Stats />} />
          <Route path="profile" element={<Profile />} />
        </Routes>
      </main>
    </div>
  );
};

export default App;